/**
 * Folha do atendimento (docs/design, "Celular: atendimento"). Tudo em no máximo 2 toques a partir
 * da agenda: concluir e cobrar (5), remarcar (3), cancelar (3), avisar (4).
 */
import {
  type PaymentMethod,
  PAYMENT_LABELS,
  activePackageFor,
  addDays,
  amountDue,
  durationMinOf,
  formatDayShort,
  formatHourOf,
  formatMoney,
  formatMoneyShort,
  paidByAppointment,
  parseMoney,
  relativeDayLabel,
  todayIn,
  toMs,
  utcToZoned,
} from '@lindeza/core';
import { useFreeSlots, useLiveQuery, useProfessional, useRuntime } from '@lindeza/core/hooks';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, toastError, withUndo } from '../../app/ui';
import { type MessageKindForAppointment, messageFor, openWhatsapp, useMessageCtx } from '../../app/useMessages';
import {
  IconBanknote,
  IconCalendarClock,
  IconCalendarX,
  IconCheck,
  IconClockAlert,
  IconCreditCard,
  IconPackage,
  IconPhone,
  IconQrCode,
  IconSquareUserRound,
  IconTriangleAlert,
  IconUserX,
  IconWhatsapp,
} from '../../components/Icons';
import { Avatar, Field, Sheet } from '../../components/ui';
import { RescheduleSheet } from './RescheduleSheet';
import { useComplete } from './useComplete';

export function AppointmentSheet({ id, onClose, onCharge }: { id: string; onClose: () => void; onCharge?: () => void }) {
  const rt = useRuntime();
  const prof = useProfessional();
  const ctx = useMessageCtx();
  const navigate = useNavigate();
  const complete = useComplete();
  const tz = prof?.timezone ?? 'America/Sao_Paulo';
  const [discount, setDiscount] = useState('');
  const [showDiscount, setShowDiscount] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [notes, setNotes] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const data = useLiveQuery(
    async (r) => {
      const a = await r.db.get('appointment', id);
      if (!a) return null;
      const [client, service, packages, appointments, payments, others] = await Promise.all([
        r.db.get('client', a.client_id),
        r.db.get('service', a.service_id),
        r.db.all('package'),
        r.db.all('appointment'),
        r.db.all('payment'),
        r.db.range('appointment', 'start_utc', a.start_utc.slice(0, 10), `${a.end_utc.slice(0, 10)}T23:59:59Z`),
      ]);
      const pkg = activePackageFor(packages, appointments, a.client_id, a.service_id, todayIn(tz));
      const paid = paidByAppointment(payments).get(a.id) ?? 0;
      const overlapping = others.filter(
        (o) => o.id !== a.id && o.status !== 'canceled' && o.status !== 'no_show' && o.start_utc < a.end_utc && a.start_utc < o.end_utc,
      );
      return { a, client, service, pkg, paid, overlapping };
    },
    [id, tz],
    ['appointment', 'client', 'service', 'package', 'payment'],
  );

  const appointment = data?.a;
  const tomorrow = addDays(todayIn(tz), 1);
  const suggestions = useFreeSlots({
    durationMin: appointment ? durationMinOf(appointment) : 60,
    location: appointment?.location ?? 'studio',
    from: todayIn(tz),
    to: addDays(todayIn(tz), 14),
    ignoreAppointmentId: id,
  });

  if (data === null) {
    return (
      <Sheet title="Atendimento" onClose={onClose}>
        <p className="muted">Esse atendimento não está neste aparelho (ainda). Se acabou de chegar, espere a sincronização.</p>
      </Sheet>
    );
  }
  if (!data || !appointment || !ctx) return null;
  const { client, service, pkg, paid } = data;
  const a = appointment;
  const local = utcToZoned(a.start_utc, tz);
  const today = todayIn(tz);
  const firstName = client?.name.split(' ')[0] ?? 'cliente';
  const active = a.status === 'scheduled' || a.status === 'confirmed';
  // Chegou a hora (ou passou): o principal é concluir e cobrar; antes disso, avisar a cliente.
  const started = toMs(a.start_utc) <= Date.now() + 15 * 60_000;
  const discountCents = parseMoney(discount) ?? 0;
  const due = Math.max(0, a.price_cents - discountCents);
  const missing = Math.max(0, amountDue(a) - paid);
  const mainKind: MessageKindForAppointment = a.status === 'completed' ? 'post_service' : local.date === tomorrow ? 'reminder' : 'confirmation';
  const mainLabel =
    mainKind === 'post_service' ? 'Agradecer e sugerir a volta' : mainKind === 'reminder' ? 'Mandar lembrete no WhatsApp' : a.status === 'confirmed' ? 'Mandar mensagem no WhatsApp' : 'Pedir confirmação no WhatsApp';

  const send = (kind: MessageKindForAppointment) => openWhatsapp(client?.whatsapp, messageFor(ctx, kind, a, client, service));

  async function run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setBusy(true);
    try {
      return await fn();
    } catch (e) {
      toastError(e);
      return undefined;
    } finally {
      setBusy(false);
    }
  }

  async function pay(method: PaymentMethod | null) {
    setBusy(true);
    const ok = await complete(a.id, method, discountCents);
    setBusy(false);
    if (ok) onClose();
  }

  // Sugestões espalhadas: até 2 por dia, com pelo menos 1 hora entre elas.
  const quick: Array<{ start: string; date: string }> = [];
  for (const d of suggestions ?? []) {
    let lastKept = -Infinity;
    let perDay = 0;
    for (const s of d.slots) {
      const t = Date.parse(s.start);
      if (s.start === a.start_utc || t - lastKept < 3_600_000 || perDay >= 2) continue;
      quick.push({ start: s.start, date: d.date });
      lastKept = t;
      perDay++;
    }
    if (quick.length >= 4) break;
  }
  quick.splice(4);

  async function moveTo(start: string) {
    const r = await run(() => rt.actions.rescheduleAppointment(a.id, start));
    if (!r) return;
    onClose();
    const when = `${relativeDayLabel(utcToZoned(start, tz).date, today).toLowerCase()} ${formatHourOf(start, tz)}`;
    toast(`Remarcado para ${when}`, {
      action: { label: 'Avisar', run: () => openWhatsapp(client?.whatsapp, messageFor(ctx!, 'reschedule', r.appointment, client, service)) },
      duration: 7000,
    });
  }

  if (rescheduling) {
    return (
      <RescheduleSheet
        title={`Remarcar ${client?.name ?? firstName}`}
        durationMin={durationMinOf(a)}
        location={a.location}
        ignoreId={a.id}
        onPick={(start) => void moveTo(start)}
        onClose={() => setRescheduling(false)}
      />
    );
  }

  const statusText =
    a.status === 'pending'
      ? { cls: 'status--warn', icon: <IconTriangleAlert />, text: 'Pedido pelo link, falta aceitar' }
      : a.status === 'confirmed'
        ? { cls: 'status--ok', icon: <IconCheck />, text: 'Confirmada' }
        : a.status === 'completed'
          ? { cls: 'status--ok', icon: <IconCheck />, text: 'Concluído' }
          : a.status === 'canceled'
            ? { cls: 'status--danger', icon: null, text: 'Cancelado' }
            : a.status === 'no_show'
              ? { cls: 'status--danger', icon: null, text: 'Faltou' }
              : a.source === 'link'
                ? { cls: '', icon: null, text: 'Marcou pelo link' }
                : { cls: 'status--warn', icon: <IconTriangleAlert />, text: 'Não confirmou' };

  return (
    <Sheet
      title={
        <span className="at-head">
          <Avatar name={client?.name ?? '?'} />
          <span className="grow">
            <button className="at-name" onClick={() => client && navigate(`/clientes/${client.id}`)}>
              {client?.name ?? 'Cliente'}
            </button>
            <span className="at-sub">
              {service?.name ?? 'Serviço'}, {relativeDayLabel(local.date, today).toLowerCase()} {formatHourOf(a.start_utc, tz)} às {formatHourOf(a.end_utc, tz)}
            </span>
          </span>
        </span>
      }
      onClose={onClose}
    >
      <div className="stack stack--lg">
        <div className="at-line">
          <span className={`status ${statusText.cls}`}>
            {statusText.icon}
            {statusText.text}
          </span>
          <span className="row">
            {a.location === 'home' && <span className="badge">Domicílio{a.address ? `: ${a.address}` : ''}</span>}
            {pkg && (
              <span className="badge">
                <IconPackage /> pacote {pkg.balance.remaining}/{pkg.balance.total}
              </span>
            )}
            <span className="price">{formatMoneyShort(a.price_cents)}</span>
          </span>
        </div>

        {client?.notes && (
          <div className="at-note">
            <IconTriangleAlert />
            <p>
              <b>Observação</b>
              {client.notes}
            </p>
          </div>
        )}

        {data.overlapping.length > 0 && !a.overlap_ok && (
          <div className="ag-clash">
            <div className="ag-clash-top">
              <IconTriangleAlert />
              <p>
                Choque com {data.overlapping.length === 1 ? 'outro atendimento' : `${data.overlapping.length} atendimentos`} às {formatHourOf(data.overlapping[0]!.start_utc, tz)}.
              </p>
            </div>
            <div className="row">
              <button className="btn btn--outline btn--sm" disabled={busy} onClick={() => void run(() => rt.actions.keepOverlap([a.id, ...data.overlapping.map((o) => o.id)]))}>
                Manter os dois
              </button>
            </div>
          </div>
        )}

        {a.status === 'pending' && (
          <div className="row">
            <button
              className="btn btn--primary btn--lg grow"
              disabled={busy}
              onClick={async () => {
                const r = await run(() => rt.actions.accept(a.id));
                if (!r) return;
                onClose();
                toast('Horário aceito.', { action: { label: 'Avisar', run: () => send('confirmation') } });
              }}
            >
              <IconCheck /> Aceitar
            </button>
            <button
              className="btn btn--outline btn--lg"
              disabled={busy}
              onClick={async () => {
                const r = await run(() => rt.actions.cancel(a.id));
                if (!r) return;
                onClose();
                withUndo('Pedido recusado.', () => rt.actions.setStatus(a.id, 'pending').then(() => undefined));
              }}
            >
              Recusar
            </button>
          </div>
        )}

        {active && started && (
          <section className="stack" aria-label="Concluir e cobrar">
            <div className="row row--between">
              <span className="at-sec-title">Concluir e cobrar</span>
              {!pkg && (
                <span className="price" data-testid="due">
                  {formatMoney(due)}
                </span>
              )}
            </div>
            {pkg ? (
              <button className="btn btn--primary btn--lg btn--block" disabled={busy} onClick={() => void pay(null)}>
                <IconPackage /> Concluir · desconta 1 sessão
              </button>
            ) : (
              <>
                <div className="pay-grid at-pay">
                  <button className="btn btn--outline btn--lg" disabled={busy} onClick={() => void pay('pix')}>
                    <IconQrCode /> Pix
                  </button>
                  <button className="btn btn--outline btn--lg" disabled={busy} onClick={() => void pay('cash')}>
                    <IconBanknote /> Dinheiro
                  </button>
                  <button className="btn btn--outline btn--lg" disabled={busy} onClick={() => void pay('card')}>
                    <IconCreditCard /> Cartão
                  </button>
                  <button className="btn btn--outline btn--lg" disabled={busy} onClick={() => void pay(null)}>
                    <IconCalendarClock /> Receber depois
                  </button>
                </div>
                {showDiscount ? (
                  <Field label="Desconto" htmlFor="discount" hint={`Fica ${formatMoney(due)}`}>
                    <input id="discount" className="input" inputMode="decimal" autoFocus placeholder="0,00" value={discount} onChange={(e) => setDiscount(e.target.value)} />
                  </Field>
                ) : (
                  <button className="btn btn--link btn--sm" onClick={() => setShowDiscount(true)}>
                    Dar desconto
                  </button>
                )}
              </>
            )}
          </section>
        )}

        {a.status === 'completed' && missing > 0 && (
          <section className="stack" aria-label="Receber">
            <div className="row row--between">
              <span className="at-sec-title">Falta receber</span>
              <span className="price">{formatMoney(missing)}</span>
            </div>
            <div className="pay-grid">
              {(['pix', 'cash', 'card'] as const).map((m) => (
                <button
                  key={m}
                  className="btn btn--outline btn--lg"
                  disabled={busy}
                  onClick={async () => {
                    const r = await run(() => rt.actions.registerPayment({ appointmentId: a.id, amountCents: missing, method: m }));
                    if (r) toast(`Recebido · ${PAYMENT_LABELS[m]} ${formatMoney(missing)}`);
                  }}
                >
                  {PAYMENT_LABELS[m]}
                </button>
              ))}
            </div>
          </section>
        )}

        {a.status !== 'pending' && a.status !== 'canceled' && a.status !== 'no_show' && !(active && started) && (
          <div className="stack">
            <p className="bubble">{messageFor(ctx, mainKind, a, client, service)}</p>
            <button className="btn btn--primary btn--lg btn--block" onClick={() => send(mainKind)}>
              <IconWhatsapp /> {mainLabel}
            </button>
          </div>
        )}

        {active && (
          <div className="at-tiles">
            <button className="at-tile" onClick={() => setRescheduling(true)}>
              <IconCalendarClock />
              Remarcar
            </button>
            <button className="at-tile" onClick={() => send('delay')}>
              <IconClockAlert />
              Avisar atraso
            </button>
            {client?.whatsapp ? (
              <a className="at-tile" href={`tel:+${client.whatsapp.replace(/\D/g, '')}`}>
                <IconPhone />
                Ligar
              </a>
            ) : (
              <span className="at-tile" aria-disabled="true">
                <IconPhone />
                Ligar
              </span>
            )}
            <button className="at-tile" onClick={() => client && navigate(`/clientes/${client.id}`)}>
              <IconSquareUserRound />
              Ver ficha
            </button>
          </div>
        )}

        {active && !started && onCharge && (
          <button className="btn btn--outline btn--block" onClick={onCharge}>
            <IconCheck /> Concluir e cobrar
          </button>
        )}

        {(active || a.status === 'pending') && quick.length > 0 && (
          <section className="stack" aria-label="Remarcar">
            <span className="at-sec-title">Remarcar para</span>
            <div className="chips">
              {quick.map((s) => (
                <button key={s.start} className="chip" disabled={busy} onClick={() => void moveTo(s.start)}>
                  {s.date === today ? 'hoje' : s.date === tomorrow ? 'amanhã' : formatDayShort(s.date).split(',')[0]} {formatHourOf(s.start, tz)}
                </button>
              ))}
              <button className="chip" onClick={() => setRescheduling(true)}>
                Outro dia…
              </button>
            </div>
          </section>
        )}

        {a.status !== 'canceled' && a.status !== 'no_show' && (
          <div className="stack">
            <span className="at-sec-title">Outras mensagens</span>
            <div className="chips at-msgs">
              {a.status === 'completed' ? (
                <button className="chip" onClick={() => send('post_service')}>
                  <IconWhatsapp /> Agradecer e sugerir volta
                </button>
              ) : (
                <>
                  <button className="chip" onClick={() => send('confirmation')}>
                    <IconWhatsapp /> Confirmação
                  </button>
                  <button className="chip" onClick={() => send('reminder')}>
                    <IconWhatsapp /> Lembrete
                  </button>
                  <button className="chip" onClick={() => send('delay')}>
                    <IconWhatsapp /> Atraso
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        <Field label="Observação" htmlFor="appt-notes">
          <textarea
            id="appt-notes"
            className="textarea"
            value={notes ?? a.notes ?? ''}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => {
              if (notes !== null && notes !== (a.notes ?? '')) void rt.actions.updateAppointmentNotes(a.id, notes);
            }}
            placeholder="Ex.: trazer esmalte, cor escolhida"
          />
        </Field>

        <div className="at-foot">
          {a.status === 'scheduled' && (
            <button className="btn btn--ghost btn--sm" disabled={busy} onClick={() => void run(() => rt.actions.confirm(a.id)).then((r) => r && toast('Confirmado.'))}>
              <IconCheck /> Cliente confirmou
            </button>
          )}
          {active && (
            <>
              <button
                className="btn btn--ghost btn--sm"
                disabled={busy}
                onClick={async () => {
                  const r = await run(() => rt.actions.markNoShow(a.id));
                  if (!r) return;
                  onClose();
                  withUndo(`${firstName} faltou.`, () => rt.actions.reopen(a.id).then(() => undefined));
                }}
              >
                <IconUserX /> Faltou
              </button>
              <button
                className="btn btn--ghost btn--sm"
                disabled={busy}
                onClick={async () => {
                  const r = await run(() => rt.actions.cancel(a.id));
                  if (!r) return;
                  onClose();
                  withUndo('Atendimento cancelado.', () => rt.actions.reopen(a.id).then(() => undefined));
                }}
              >
                <IconCalendarX /> Cancelar atendimento
              </button>
            </>
          )}
          {(a.status === 'canceled' || a.status === 'no_show') && (
            <button className="btn btn--outline" disabled={busy} onClick={() => void run(() => rt.actions.reopen(a.id)).then((r) => r && toast('Atendimento reaberto.'))}>
              Reabrir
            </button>
          )}
          {a.status === 'completed' && (
            <button
              className="btn btn--ghost btn--sm"
              disabled={busy}
              onClick={async () => {
                const r = await run(() => rt.actions.reverseCompletion(a.id));
                if (r) toast('Conclusão estornada: o atendimento voltou para agendado.');
              }}
            >
              Estornar conclusão
            </button>
          )}
        </div>
      </div>
    </Sheet>
  );
}
